// Sanitización del HTML del cuerpo de un email antes de inyectarlo en el
// timeline del lead (panel Actividad). Portada del CRM
// (crm/apps/web/src/features/timeline/lib/sanitizeEmailHtml.ts), que usa
// isomorphic-dompurify con este criterio:
//
//   DOMPurify.sanitize(html, {
//     FORBID_TAGS: ['script','style','iframe','object','embed','form'],
//     FORBID_ATTR: ['onerror','onload','onclick','onmouseover','onmouseout',
//                   'onfocus','onblur'],
//     ALLOW_DATA_ATTR: false,
//   })
//
// Este SPA no tiene DOMPurify en su dependency tree (y no queremos añadir una
// dependencia solo para esto), así que replicamos el MISMO criterio con el
// parser del propio navegador: quitamos los tags prohibidos, TODOS los atributos
// on* (superset del denylist explícito del CRM), los atributos data-*, y además
// neutralizamos los esquemas peligrosos en href/src (javascript:, vbscript:,
// data: en href) — vectores que los defaults de DOMPurify ya bloquean y que aquí
// cubrimos a mano para no quedarnos por debajo de su nivel de seguridad.
//
// El resultado se renderiza SIEMPRE en un contenedor aislado (iframe sandbox),
// nunca inyectado con dangerouslySetInnerHTML en el árbol de la app.

const FORBIDDEN_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  // Extra frente al CRM: estos tampoco tienen sentido en el cuerpo de un email
  // renderizado y son vectores conocidos; DOMPurify también los trataría.
  "link",
  "meta",
  "base",
  "noscript",
]);

// Esquemas de URL permitidos en href/src. Todo lo demás (javascript:, vbscript:,
// file:, etc.) se elimina. `data:` se permite solo en src (imágenes inline de
// email), nunca en href. `attachment:` (imágenes privadas de una nota rica)
// solo se admite en src y solo cuando el llamante lo pide (sanitizeNoteHtml).
function isSafeUrl(
  value: string,
  attr: "href" | "src",
  allowAttachmentScheme: boolean,
): boolean {
  const v = value.trim().toLowerCase();
  // URLs relativas o anclas: seguras.
  if (
    v.startsWith("#") ||
    v.startsWith("/") ||
    v.startsWith("./") ||
    v.startsWith("../")
  ) {
    return true;
  }
  if (v.startsWith("http://") || v.startsWith("https://")) return true;
  if (v.startsWith("mailto:") || v.startsWith("tel:")) return true;
  if (attr === "src" && v.startsWith("data:image/")) return true;
  if (allowAttachmentScheme && attr === "src" && v.startsWith("attachment:")) {
    return true;
  }
  // Sin esquema explícito y no relativa (p.ej. "www.x.com"): la tratamos como
  // segura (el navegador la resolverá como relativa); los esquemas peligrosos
  // siempre llevan ":" antes de cualquier "/".
  if (!/^[a-z][a-z0-9+.-]*:/.test(v)) return true;
  return false;
}

function sanitizeElement(el: Element, allowAttachmentScheme: boolean): void {
  // Copia estática: vamos a mutar el árbol mientras iteramos.
  const children = Array.from(el.children);
  for (const child of children) {
    const tag = child.tagName.toLowerCase();
    if (FORBIDDEN_TAGS.has(tag)) {
      child.remove();
      continue;
    }
    // Limpia atributos peligrosos.
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        child.removeAttribute(attr.name);
        continue;
      }
      if (name.startsWith("data-")) {
        child.removeAttribute(attr.name);
        continue;
      }
      // srcset puede acarrear URLs alternativas que isSafeUrl no ve. Solo se
      // poda en las NOTAS (nuestro editor nunca lo genera y se inyectan
      // inline); en los emails se conserva — van a un iframe sandbox y hay
      // plantillas de ESP que dependen de él para pintar la imagen.
      if (name === "srcset" && allowAttachmentScheme) {
        child.removeAttribute(attr.name);
        continue;
      }
      if (name === "href" || name === "src") {
        if (!isSafeUrl(attr.value, name, allowAttachmentScheme)) {
          child.removeAttribute(attr.name);
        }
      }
    }
    sanitizeElement(child, allowAttachmentScheme);
  }
}

export function sanitizeEmailHtml(html: string): string {
  if (typeof window === "undefined" || !window.document) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  sanitizeElement(doc.body, false);
  return doc.body.innerHTML;
}

// Sanitización del HTML de una nota ENRIQUECIDA de ticket antes de renderizarlo
// (RichNoteBody). Mismo criterio que sanitizeEmailHtml — el HTML persistido se
// sanitiza SIEMPRE al render, nunca se confía en el almacenado — con una
// diferencia: el esquema `attachment:` está permitido en src, que es como las
// notas referencian sus imágenes privadas (`<img src="attachment:filename">`),
// resueltas después a un blob autenticado. Réplica del sanitizeNoteHtml del CRM
// (crm/apps/web/src/shared/notes/sanitizeNoteHtml.ts, DOMPurify + attachment:).
//
// OJO: a diferencia del email (iframe sandbox), la nota rica se inyecta INLINE
// en el árbol de la app (dangerouslySetInnerHTML) — mismo modelo que el CRM.
// Por eso este sanitizador es estricto: tags de script/embed fuera, todos los
// on* fuera, esquemas de URL en allowlist.
export function sanitizeNoteHtml(html: string): string {
  if (typeof window === "undefined" || !window.document) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  sanitizeElement(doc.body, true);
  return doc.body.innerHTML;
}
